import { useEffect, useMemo, useState } from "react";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { filtrar, listarOcupacoes, type Ocupacao } from "../lib/ocupacoes";

/**
 * "Posso ser MEI com esta atividade?" — a busca nas 502 ocupações (000086).
 *
 * ## Por que uma lista só, e não duas telas
 *
 * No Bubble são duas tabelas (471 permitidas, 31 vedadas) e o contador tinha de
 * saber ONDE procurar antes de procurar. Mas a pergunta dele é uma: *"o meu
 * cliente pode?"* — e a resposta útil é encontrar a atividade e ver o selo. Duas
 * buscas separadas fazem com que "não achei nas permitidas" pareça resposta
 * ("então pode?"), quando a atividade podia estar na lista de vedadas ao lado.
 *
 * ## O que a tela NÃO afirma
 *
 * Não achar nada não significa "não pode". A lista é a das ocupações previstas
 * na norma; atividade que não está em nenhuma das duas listas simplesmente não
 * tem resposta aqui. O aviso do rodapé diz isso com letra, junto com a data da
 * lista — porque uma lista de referência sem idade visível é a que envelhece
 * sem ninguém notar. Ela é de 2023 e passou a ser editável pela equipe, e o
 * contador tem direito de saber os dois fatos antes de responder ao cliente.
 *
 * ## Sem estado na URL
 *
 * Ao contrário das páginas de conteúdo (onde a rota é o endereço que se
 * compartilha), aqui o que se compartilha é a FERRAMENTA, não a busca: ninguém
 * manda ao colega "a consulta de açougueiro". Termo em `useState` é o certo.
 *
 * ## O redesenho de 26/08: a busca é a ferramenta, e 502 caixas não são uma lista
 *
 * Medido na captura do antes: **64.608 px de altura — 72 telas —, 504 caixas, e
 * "Pode ser MEI" escrito 471 vezes**, porque a tela desenhava as 502 ocupações
 * de uma vez, antes de qualquer busca. Ninguém lê 502 linhas; a tela existe para
 * digitar "açougue" e ver uma. Então:
 *
 *   · a busca mora DENTRO da cabine navy, com os três filtros ao lado — é a
 *     primeira coisa da tela porque é a única coisa da tela;
 *   · sem termo, a lista mostra as primeiras 30 e um botão para ver mais. Com
 *     termo, mostra tudo o que casou (uma busca nunca casa 500). A contagem no
 *     `role="status"` é sempre a dos RESULTADOS, não a das linhas desenhadas —
 *     é ela que a prova lê;
 *   · o selo deixou de gritar em cada linha: "Pode ser MEI" 471 vezes não
 *     informa nada. Agora o selo é um ponto colorido + o texto curto, e a linha
 *     vedada é a que se destaca, porque é ela que muda a resposta ao cliente.
 */
const PRIMEIRAS = 30;
const MAIS = 60;

export default function MeiOcupacoes() {
  const [lista, setLista] = useState<Ocupacao[] | null>(null);
  const [erro, setErro] = useState(false);
  const [termo, setTermo] = useState("");
  const [tipo, setTipo] = useState<"todas" | "permitidas" | "vedadas">("todas");
  const [limite, setLimite] = useState(PRIMEIRAS);

  useEffect(() => {
    void (async () => {
      const r = await listarOcupacoes();
      if (r === null) setErro(true);
      else setLista(r);
    })();
  }, []);

  const achadas = useMemo(() => {
    if (!lista) return [];
    const porTipo =
      tipo === "todas" ? lista : lista.filter((o) => (tipo === "vedadas" ? o.vedada : !o.vedada));
    return filtrar(porTipo, termo);
  }, [lista, termo, tipo]);

  // Mudou a busca ou o filtro: a janela volta ao começo.
  useEffect(() => {
    setLimite(PRIMEIRAS);
  }, [termo, tipo]);

  if (erro) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <p className="rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar a lista de ocupações agora. Recarregue a página; se
          continuar, fale com o suporte.
        </p>
      </main>
    );
  }
  if (!lista) return <Carregando />;

  const permitidas = lista.filter((o) => !o.vedada).length;
  const vedadas = lista.length - permitidas;
  const buscando = termo.trim() !== "";
  const visiveis = buscando ? achadas : achadas.slice(0, limite);
  const escondidas = achadas.length - visiveis.length;

  const chip = (ativo: boolean) =>
    `rounded-full px-4 py-1.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40 ${
      ativo ? "bg-laranja text-azul" : "border border-navy-mute/30 text-navy-ink hover:bg-white/10"
    }`;

  return (
    <main className="mx-auto max-w-5xl px-4 py-7 sm:px-6">
      <Cabine
        trilha={{ para: "/ferramenta/mei-pro", rotulo: "MEI PRO" }}
        rotulo="Consulta"
        titulo="Atividades permitidas e vedadas"
        descricao="Procure pelo nome da atividade ou pelo código CNAE para saber se ela pode ser MEI, e se paga ICMS ou ISS."
        rodape={
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase">
                Atividade ou CNAE
              </span>
              <input
                className="h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-4 text-base text-white placeholder:text-navy-mute/70 focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
                type="search"
                autoFocus
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                placeholder="açougueiro, cabeleireiro, 4789-0/99…"
              />
            </label>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por tipo">
              {(
                [
                  ["todas", `Todas (${lista.length})`],
                  ["permitidas", `Permitidas (${permitidas})`],
                  ["vedadas", `Vedadas (${vedadas})`],
                ] as const
              ).map(([valor, rotulo]) => (
                <button
                  key={valor}
                  type="button"
                  aria-pressed={tipo === valor}
                  onClick={() => setTipo(valor)}
                  className={chip(tipo === valor)}
                >
                  {rotulo}
                </button>
              ))}
            </div>
          </div>
        }
      />

      <p role="status" className="mt-6 text-sm text-cinza">
        {achadas.length === 0
          ? "Nenhuma atividade encontrada."
          : `${achadas.length} ${achadas.length === 1 ? "atividade" : "atividades"}${
              escondidas > 0 ? ` · mostrando as primeiras ${visiveis.length}` : ""
            }`}
      </p>

      {achadas.length === 0 && buscando && (
        <div className="mt-3 rounded-xl bg-superficie-2 p-5 text-sm leading-relaxed text-cinza">
          <p className="font-semibold text-titulo">Não achar aqui não quer dizer que não pode.</p>
          <p className="mt-1">
            Esta é a lista das ocupações previstas na norma, com o nome que ela usa
            — que às vezes é diferente do nome do dia a dia. Tente uma palavra só
            (&ldquo;cabelo&rdquo; em vez de &ldquo;salão de beleza&rdquo;) ou o
            código CNAE da atividade.
          </p>
        </div>
      )}

      <ul className="mt-3 divide-y divide-borda overflow-hidden rounded-cf-lg border border-borda bg-superficie">
        {visiveis.map((o) => (
          <li
            key={o.id}
            className={`flex flex-wrap items-start gap-x-4 gap-y-1.5 px-4 py-3 sm:px-5 ${
              o.vedada ? "bg-erro-fundo/40" : ""
            }`}
          >
            <span
              aria-hidden="true"
              className={`mt-2 size-2 shrink-0 rounded-full ${o.vedada ? "bg-erro-tinta" : "bg-sucesso"}`}
            />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-titulo">{o.atividade}</p>
              {o.descricao && (
                <p className="mt-0.5 text-sm leading-relaxed text-cinza">{o.descricao}</p>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-cinza">
                {o.cnae && <span className="tabular-nums">CNAE {o.cnae}</span>}
                {/* Nulo é "a lista não diz", que é diferente de "não paga" — e a
                    diferença importa para quem vai orientar o cliente. */}
                {o.icms !== null && <span>ICMS: {o.icms ? "sim" : "não"}</span>}
                {o.iss !== null && <span>ISS: {o.iss ? "sim" : "não"}</span>}
                {o.exclusao && <span>excluída por {o.exclusao}</span>}
              </div>
            </div>
            <span
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
                o.vedada ? "bg-erro-fundo text-erro-tinta" : "bg-aviso-fundo text-aviso-tinta"
              }`}
            >
              {o.vedada ? "Não pode ser MEI" : "Pode ser MEI"}
            </span>
          </li>
        ))}
      </ul>

      {escondidas > 0 && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setLimite((l) => l + MAIS)}
            className="btn-contorno text-sm"
          >
            Mostrar mais {Math.min(MAIS, escondidas)} de {escondidas}
          </button>
        </div>
      )}

      <p className="mt-8 rounded-xl bg-superficie-2 p-4 text-xs leading-relaxed text-cinza">
        A lista veio do sistema antigo, onde foi cadastrada em 2023, e agora é
        mantida pela equipe da Contabilidade Facilitada. Antes de orientar um
        cliente, confira a ocupação no Portal do Empreendedor: a norma do MEI muda
        por resolução do CGSN, e atividade entra e sai da lista.
      </p>
    </main>
  );
}
